// ─────────────────────────────────────────────────────────────
// Pricing: frontend-only presentation extras + a fallback used when
// `GET /api/billing/plans` fails (offline / slow network). All numeric
// prices + copy come from the backend at runtime — see
// `mergePlans()` below and `Landing.jsx::useLandingPlans`.
//
// Phase E (2026-02): 3-way Monthly | Bi-Annual | Annual toggle.
// The Free card is intentionally OUTSIDE the interval selector — it stays
// $0 / 30-day-trial always. Paid plans show interval-derived pricing,
// benefits, and (for Annual only) crossed-out reference + Save 25% badge.
// ─────────────────────────────────────────────────────────────

// Presentation extras keyed by plan id. These live in the frontend because
// they are pure marketing decisions (ribbon copy, CTA label, "most popular"
// highlight). Domain badges have been removed — the interval-derived
// benefit line ("Free standard domain included") is now data-driven from
// `plan.pricing[interval].free_domain_first_site`.
export const PLAN_EXTRAS = {
  free:   { cta: "Start Free", note: "for 30 days", ribbon: "FREE" },
  launch: { cta: "Choose Launch" },
  growth: { cta: "Choose Growth", popular: true },
  scale:  { cta: "Choose Scale" },
};

// Fallback plans — mirror the backend PLANS shape exactly so that if the
// billing endpoint fails, the landing page still shows the correct prices.
// The fallback carries the same `pricing` structure the backend emits.
const _pricing = (monthlyAmt, halfAmt, annualAmt, refAmt, eqMo) => ({
  monthly:  { amount_usd: monthlyAmt, period_months: 1,
              free_domain_first_site: false, non_refundable: false,
              reference_amount_usd: null, discount_percent: null,
              equivalent_monthly_usd: monthlyAmt },
  biannual: { amount_usd: halfAmt, period_months: 6,
              free_domain_first_site: true, non_refundable: false,
              reference_amount_usd: null, discount_percent: null,
              equivalent_monthly_usd: monthlyAmt },
  annual:   { amount_usd: annualAmt, period_months: 12,
              free_domain_first_site: true, non_refundable: true,
              reference_amount_usd: refAmt, discount_percent: 25,
              equivalent_monthly_usd: eqMo },
});

export const FALLBACK_PLANS = [
  { id: "free", name: "Free", price: 0, pricing: null,
    tagline: "Best for getting started.",
    primary: ["AI website + 30-day access", "Free subdomain included", "Stripe Connect payments"],
    features: [], missing: [] },
  { id: "launch", name: "Launch", price: 25,
    pricing: _pricing(25, 150, 225, 300, 18.75),
    tagline: "Best for launching your first business.",
    primary: ["Up to 10 businesses", "Audience + 30-day plan", "1 team seat"],
    features: [], missing: [] },
  { id: "growth", name: "Growth", price: 45,
    pricing: _pricing(45, 270, 405, 540, 33.75),
    tagline: "Best for growing your launch.",
    primary: ["Unlimited businesses", "2 team seats", "Standard support"],
    features: [], missing: [] },
  { id: "scale", name: "Scale", price: 99,
    pricing: _pricing(99, 594, 891, 1188, 74.25),
    tagline: "Best for scaling faster.",
    primary: ["Unlimited businesses", "3 team seats", "Priority support"],
    features: [], missing: [] },
];

export const FALLBACK_INTERVALS = [
  { id: "monthly",  label: "Monthly",   period_months: 1,  badge: null },
  { id: "biannual", label: "Bi-Annual", period_months: 6,  badge: null },
  { id: "annual",   label: "Annual",    period_months: 12, badge: "Save 25%" },
];

/**
 * Merge backend `/api/billing/plans` response with frontend presentation
 * extras. Returns a list shaped for `<PricingCard/>`.
 *
 * @param {Array} backendPlans - `data.plans` from GET /api/billing/plans
 */
export function mergePlans(backendPlans) {
  if (!Array.isArray(backendPlans) || backendPlans.length === 0) return FALLBACK_PLANS;
  return backendPlans.map((p) => {
    const extra = PLAN_EXTRAS[p.id] || {};
    return {
      id: p.id,
      name: p.name,
      price: p.price_usd,           // legacy monthly price (BC)
      pricing: p.pricing || null,   // new interval-aware structure
      tagline: p.tagline,
      primary: p.primary_features || [],
      features: p.features || [],
      missing: p.missing || [],
      ...extra,
    };
  });
}

/**
 * Return the price object for a given (plan, interval). Falls back to
 * monthly if the interval isn't present (Free plan has no `pricing` and
 * should short-circuit before calling this).
 */
export function pickPricing(plan, intervalId) {
  if (!plan?.pricing) return null;
  return plan.pricing[intervalId] || plan.pricing.monthly || null;
}
